import React, { useState, useEffect, useContext, useRef } from "react";
import ListAndAddTabs from "components/Layout/ListAndAddTabs";
import Page from "components/Page";
import { GET_POI_TYPES } from "lib/pois";
import { useMutation, useQuery } from "@apollo/client";
import update from "immutability-helper";
import {
  GetFloorOverlaysAndroidQueryResult,
  GetPoiTypesQueryResult,
  FloorOverlay,
  PoiType,
  GetAllZonesQueryResult,
  InsertGeoFenceMutationVariables,
  Zone_Insert_Input,
  RoleGeoFence_Insert_Input,
} from "generated/graphql";
import {
  Checkbox,
  Grid,
  Typography,
  Card,
  LinearProgress,
} from "@material-ui/core";
import styled from "styled-components";
import PoiMap, {
  PoiMapProps,
} from "Modules/BuildingConfig/PointsOfInterest/PoiMap";
import { GET_FLOOR_OVERLAYS_ANDROID } from "lib/buildings";
import { BuildingContext } from "Context/BuildingProvider";
import { StandardContainer } from "components/Layout/StandardStyledComponents";
import AddEditPoi from "Modules/BuildingConfig/PointsOfInterest/AddEditPoi";
import { AuthContext } from "Firebase/AuthProvider";
import { GET_ALL_ZONES, INSERT_GEOFENCE } from "lib/zones";
import ZonesTable from "Modules/Zones/ZonesTable";
import BasicGeoFenceConfig from "Modules/Zones/BasicGeoFenceConfig";
import ZoneAddMap from "Modules/Zones/ZoneAddMap";
import HolesAddMap from "Modules/Zones/HolesAddMap";
import StepperFrame, { StepFrame } from "components/Layout/StepperFrame";
import { LocationPosition } from "Modules/ShiftPlayback/PlaybackLocationsLoader";
import GeoFenceRoleChecklist from "Modules/Zones/GeoFenceRoleChecklist";
interface ZonesState {
  currentFloorNumber: number;
  editing: boolean;
  tabValue: number;
  name: string;
  boundary?: LocationPosition[] | null;
  holes: LocationPosition[][];
  holesCount: number;
  selectedRoles: number[];
}

const DEFAULT_STATE: ZonesState = {
  currentFloorNumber: 0,
  editing: false,
  tabValue: 0,
  name: "",
  boundary: null,
  holes: [],
  holesCount: 0,
  selectedRoles: [],
};

export default function Zones() {
  const [state, setState] = useState(DEFAULT_STATE);
  const { currentBuilding, updateBuilding } = useContext(BuildingContext);
  const { setRoute } = useContext(AuthContext);
  const boundaryRef = useRef();
  const holesRef = useRef();
  const { loading, error, data }: GetAllZonesQueryResult = useQuery(
    GET_ALL_ZONES,
    {
      variables: {
        buildingId: currentBuilding?.Id,
      },
    }
  );
  const [insertGeoFence] =
    useMutation<InsertGeoFenceMutationVariables>(INSERT_GEOFENCE);
  const floorOverlaysQueryResult: GetFloorOverlaysAndroidQueryResult = useQuery(
    GET_FLOOR_OVERLAYS_ANDROID,
    {
      variables: {
        buildingId: currentBuilding?.Id,
      },
    }
  );
  useEffect(() => {
    setRoute("Zones");
  }, []);

  const handleTabChange = (newValue: number) => {
    setState({ ...state, tabValue: newValue });
  };

  const onSubmit = async () => {
    const zones: Zone_Insert_Input[] = [];
    zones.push({
      buildingId: currentBuilding?.Id,
      points: state.boundary,
      allowed: true,
    });
    state.holes.forEach((hole) => {
      zones.push({
        buildingId: currentBuilding?.Id,
        points: hole,
        allowed: false,
      });
    });
    const roles: RoleGeoFence_Insert_Input[] = [];
    state.selectedRoles.forEach((roleId) => {
      roles.push({
        roleId,
      });
    });
    try {
      const result = await insertGeoFence({
        variables: {
          buildingId: currentBuilding?.Id,
          name: state.name,
          zones: { data: zones },
          roleGeoFences: { data: roles },
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  const onBoundaryAdd = (latLngs: LocationPosition[]) => {
    setState({ ...state, boundary: latLngs });
  };

  const onHoleAdd = (latLngs: LocationPosition[]) => {
    const newHoles = state.holes;
    const hole = latLngs.reverse();
    newHoles.push(hole);

    setState(
      update(state, {
        holes: { $set: newHoles },
        holesCount: { $set: state.holesCount++ },
      })
    );
  };

  const onHoleRemove = () => {};
  const onBoundaryRemove = () => {};

  const handleRoleClick = (id: string | number) => {
    if (typeof id === "string") {
      id = parseInt(id);
    }
    if (state.selectedRoles.includes(id)) {
      const newRoles = state.selectedRoles.filter((rts) => {
        return rts !== id;
      });
      setState((prevState) => ({
        ...prevState,
        selectedRoles: newRoles,
      }));
    } else {
      const newRoles = state.selectedRoles;
      newRoles.push(id);
      setState((prevState) => ({
        ...prevState,
        selectedRoles: newRoles,
      }));
    }
  };
  if (loading) {
    return <LinearProgress />;
  } else if (error) {
    return <div>{error.message}</div>;
  } else if (!!floorOverlaysQueryResult.data?.FloorOverlay) {
    const list: JSX.Element = <ZonesTable />;
    const stepFrames: StepFrame[] = [
      {
        label: "Name The Geo Fence",
        children: (
          <BasicGeoFenceConfig
            handleZoneNameChange={(name) => {
              setState({ ...state, name });
            }}
          />
        ),
      },
      {
        label: "Create the boundary",
        children: (
          <ZoneAddMap
            innerRef={boundaryRef}
            mapId="boundary"
            boundary={state.boundary}
            holes={state.holes}
            onZoneAdd={onBoundaryAdd}
            floorOverlays={
              floorOverlaysQueryResult.data.FloorOverlay as unknown as Pick<
                FloorOverlay,
                | "BuildingId"
                | "CenterLat"
                | "CenterLng"
                | "D_diff"
                | "FloorNumber"
                | "Id"
                | "ImageUrl"
                | "Name"
                | "Rot"
                | "created_at"
                | "k_diff"
                | "updated_at"
                | "AndroidFloors"
              >[]
            }
          />
        ),
      },
      {
        label: "Add No-Go Zones",
        children: (
          <HolesAddMap
            innerRef={holesRef}
            mapId="holes"
            holesCount={state.holesCount}
            boundary={state.boundary}
            holes={state.holes}
            onZoneAdd={onHoleAdd}
            floorOverlays={
              floorOverlaysQueryResult.data.FloorOverlay as unknown as Pick<
                FloorOverlay,
                | "BuildingId"
                | "CenterLat"
                | "CenterLng"
                | "D_diff"
                | "FloorNumber"
                | "Id"
                | "ImageUrl"
                | "Name"
                | "Rot"
                | "created_at"
                | "k_diff"
                | "updated_at"
                | "AndroidFloors"
              >[]
            }
          />
        ),
      },
      {
        label: "Assign Roles",
        children: (
          <GeoFenceRoleChecklist
            handleRoleClick={handleRoleClick}
            selectedRoles={state.selectedRoles}
          />
        ),
      },
    ];
    const add = (
      <StepperFrame stepFrames={stepFrames} handleSubmit={onSubmit} />
    );
    return (
      <Page title="Zones">
        <ListAndAddTabs
          value={state.tabValue}
          handleChange={handleTabChange}
          listChildren={list}
          addChildren={add}
          fromEdit={state.editing}
        />
      </Page>
    );
  } else {
    return <div>error</div>;
  }
}
